import { useSelector } from 'react-redux';
import { Field, NumericInput, TextOutput, RadioGroup, Checkbox } from '../../../../Core';
import { PageSection, ListFieldSection } from '../../../../Layout';
import { WorksheetAssociatedTestSection } from '../../../Core';
import { withInjectedProps, NamePrefixModifier } from '../../../../Core/InjectedPropsContext';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { Guid } from 'Lib/Guid';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import { hasValue } from 'Lib/Any';
import { DryMassesDetermination } from 'Components/Worksheets/OversizeDeterminationWorksheet/types';
import externalTestMethods from 'Components/Worksheets/ExternalTestMethods';

let OversizeCorrectionSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const visible = useSelector(createWorksheetSelector!('data.Preparation.OversizeCorrectionRequired', false));

  const importedOversizeMoistureTestId = useSelector(
    createWorksheetSelector!('AssociatedOversizeMoisture.ImportedTest.Id')
  ) as Guid;
  const importedSpecificGravityCoarseTestId = useSelector(
    createWorksheetSelector!('AssociatedSpecificGravityCoarse.ImportedTest.Id')
  ) as Guid;
  const useAbsorptionAsOversizeMoisture = (useSelector(createWorksheetSelector!('UseAbsorptionAsOversizeMoisture')) ??
    false) as boolean;
  const hideMaterialTakenAs = useSelector(
    combine(
      [
        'data.Preparation.AssociatedOversizeDetermination.ImportedTest',
        'data.Preparation.AssociatedOversizeDetermination.ExternalTest',
        'data.Preparation.AssociatedOversizeDetermination.MeasuredTest.DryMassesDetermination',
      ].map((n) => createWorksheetSelector!(n, false)),
      (imported, external, measuredDryMassDetermination: DryMassesDetermination) =>
        hasValue(imported) || hasValue(external) || measuredDryMassDetermination === 'coarse_fine_moisture_content'
    )
  );

  const oversizeMoistureAlreadyDetermined = useSelector(
    pipe(
      createWorksheetSelector!(
        'data.Preparation.AssociatedOversizeDetermination.MeasuredTest.DryMassesDetermination',
        false
      ),
      (d: DryMassesDetermination) => d === 'coarse_fine_moisture_content'
    )
  );
  const specificGravityIsAssumed = useSelector(createWorksheetSelector!('SpecificGravityCoarseAssumed'));
  const specificGravityOvenDry = useSelector(
    createWorksheetSelector!('SpecificGravityCoarseResults.SpecificGravityOvenDry')
  );
  const hasOversizeMoistureContent = useSelector(pipe(createWorksheetSelector!('OversizeMoisturePercent'), hasValue));

  const showUseAbsorptionCheckbox = !oversizeMoistureAlreadyDetermined;

  return !visible ? null : (
    <PageSection heading="Oversize Correction Data">
      <ListFieldSection single>
        <Field
          input={<RadioGroup />}
          label="Specific Gravity of Coarse Material Determined By"
          name="SpecificGravityCoarseAssumed"
          confirm={() => (specificGravityIsAssumed ? !!specificGravityOvenDry : true)}
          confirmTitle="Confirm Determination Change"
          confirmText="Any existing data for this results determination will be removed."
          confirmButtonText="Change Determination"
          options={[
            { label: 'Assumed', value: true },
            { label: 'Measured', value: false },
          ]}
        />
        <Field
          input={<NumericInput />}
          label="Specific Gravity (Oven Dry)"
          name="AssumedSpecificGravity"
          visible={specificGravityIsAssumed}
        />
        <Field
          input={<NumericInput />}
          label="Absorption (%)"
          name="AssumedAbsorption"
          visible={specificGravityIsAssumed}
        />
      </ListFieldSection>
      {!specificGravityIsAssumed && (
        <WorksheetAssociatedTestSection
          heading="Specific Gravity of Coarse Material"
          label="Method of Determination:"
          name="AssociatedSpecificGravityCoarse"
          testType="Specific Gravity Coarse"
          associatedTestModel={{
            measured: {
              measuredTestSetup: (
                <ListFieldSection visible={!hideMaterialTakenAs} wide>
                  <Field
                    input={<RadioGroup />}
                    label="Material Taken as"
                    name="MeasuredSpecificGravityCoarseMaterialSource"
                    options={[
                      { label: 'Full sample retained on oversize sieve', value: 'oversize_full_sample' },
                      { label: 'Sub sample of material retained on oversize sieve', value: 'oversize_sub_sample' },
                    ]}
                  />
                </ListFieldSection>
              ),
            },
            imported: {
              importedTestResults: (
                <ListFieldSection>
                  <Field
                    input={<TextOutput />}
                    label="Specific Gravity (Oven Dry)"
                    name="SpecificGravityCoarseResults.SpecificGravityOvenDry"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Specific Gravity (SSD)"
                    name="SpecificGravityCoarseResults.SpecificGravitySSD"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Apparent Specific Gravity"
                    name="SpecificGravityCoarseResults.ApparentSpecificGravity"
                    emph
                  />
                  <Field
                    input={<TextOutput />}
                    label="Absorption (%)"
                    name="SpecificGravityCoarseResults.Absorption"
                    emph
                  />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedSpecificGravityCoarseTestId,
              },
            },
            external: {
              testMethods: [externalTestMethods.specificGravity.AASHTO_T_85],
              externalTestResults: (
                <ListFieldSection single>
                  <Field
                    input={<NumericInput />}
                    label="Specific Gravity (Oven Dry)"
                    name="AssociatedSpecificGravityCoarse.ExternalTest.Results.SpecificGravityOvenDry"
                    emph
                  />
                  <Field
                    input={<NumericInput />}
                    label="Absorption (%)"
                    name="AssociatedSpecificGravityCoarse.ExternalTest.Results.Absorption"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      )}
      <NamePrefixModifier namePrefix="data">
        <Field
          input={<TextOutput />}
          label="Moisture Content (%)"
          name="Preparation.ScreeningASTM.OversizeMoistureContent"
          visible={oversizeMoistureAlreadyDetermined}
          emph
        />
      </NamePrefixModifier>
      <Field
        visible={showUseAbsorptionCheckbox}
        input={<Checkbox />}
        label="Use Absorption as Coarse Material Moisture Content"
        name="UseAbsorptionAsOversizeMoisture"
        confirm={() => (!useAbsorptionAsOversizeMoisture ? !!hasOversizeMoistureContent : false)}
        confirmTitle="Confirm Moisture Content Determination Change"
        confirmText="Any existing data for the coarse material moisture content will be removed."
        confirmButtonText="Change Determination"
        emph
      />
      {!oversizeMoistureAlreadyDetermined && !useAbsorptionAsOversizeMoisture ? (
        <WorksheetAssociatedTestSection
          heading="Coarse Material Moisture Content"
          label="Coarse Material Moisture Content Determined by:"
          name="AssociatedOversizeMoisture"
          testType="Moisture Content"
          associatedTestModel={{
            measured: true,
            imported: {
              importedTestResults: (
                <NamePrefixModifier namePrefix="data">
                  <ListFieldSection>
                    <Field
                      input={<TextOutput />}
                      label="Moisture Content (%)"
                      name="Preparation.ScreeningASTM.OversizeMoistureContent"
                      emph
                    />
                  </ListFieldSection>
                </NamePrefixModifier>
              ),
              importModel: {
                Id: importedOversizeMoistureTestId,
              },
            },
            external: {
              permitOtherTestMethods: true,
              testMethods: [externalTestMethods.moistureContent.AASHTO_T_265],
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    label="Moisture Content (%)"
                    name="AssociatedOversizeMoisture.ExternalTest.Results.MoistureContentPercent"
                    emph
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      ) : null}
    </PageSection>
  );
};

OversizeCorrectionSection = withInjectedProps(OversizeCorrectionSection);

export { OversizeCorrectionSection };
export default OversizeCorrectionSection;
